/* eslint-disable react/no-string-refs */
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { hooks, media } from '@lifesize/clients.sdk';
import { ReactComponent as CancelIcon } from '@lifesize/ux-assets/vectors/svgIcons/cancel.svg';
import incomingRing from 'assets/audio/incoming-ring.mp3';
import Button from 'components/Button/Button';
import LockedMeetingTooltip from 'components/Tooltip/LockedMeetingTooltip';
import PulsingAvatar from 'components/PulsingAvatar/PulsingAvatar';
import { IconType } from 'components/PulsingAvatar/AvatarIcon';
import CallPreview from 'components/CallPreview/CallPreview';
import AudioElement from 'containers/AudioElement/AudioElementContainer';
import { useSelector, useDispatch } from 'react-redux';
import _get from 'lodash/get';
import { callActions, contactSelectors, featureSelectors, remoteModeratorActions } from '@lifesize/nucleus';
import { closeFullScreenModal } from 'actions/FullScreenModal';
import { logger } from 'utils/logger';
import { FLAG_VIRTUAL_BACKGROUND } from 'constants/FeatureFlags';
import { acquireWebcam } from 'actions/media';
import { isAcquiringSelector } from 'selectors/media';
import classes from './IncomingCallModal.module.scss';
import { stopWebcam } from 'actions/media';

/**
 * This class is used as the holding component for all full screen modals
 * in the app. We just perform a simple switch on the subcomponent type to be
 * rendered in the full screen modal.
 */

const IncomingCallModal = () => {
  const dispatch = useDispatch();
  const contactSelector = useSelector(contactSelectors.getContactByDialStringGenerator);
  const isAcquiring = useSelector(isAcquiringSelector);
  const localPrimaryStream = hooks.useLocalPrimaryStream()?.stream;
  const incomingCalls = hooks.useIncomingCalls();
  const isUserModerator = hooks.useIsCurrentUserModerator();
  const isLocked = hooks.useIsWaitingRoomEnabled();
  const remoteModerationIsActive = hooks.useIsRemoteModerationActive();
  const isCallConnected = hooks.useCallConnected();
  const mediaSettings = useSelector((state) => state?.app?.mediaSettings);
  const vbbFeatureEnabled = useSelector((state) =>
    featureSelectors.getFeatureFlag(state)(FLAG_VIRTUAL_BACKGROUND)
  );
  const { selection: bgSelection } = useSelector((state) => _get(state, 'app.vbbSettings', {}));

  const defaultAudioMute = mediaSettings?.localAudioMuted;
  const defaultVideoMute = mediaSettings?.localVideoMuted;
  const call = incomingCalls?.[0];
  const incomingCall = call;
  const isVisible = !!call;
  const isLockedByModerator = isLocked && !isUserModerator;
  const dialString = _get(incomingCall, 'callerProperties.extension');
  let contact = null;
  if (dialString) {
    contact = contactSelector(dialString);
  }
  const isIncomingCallMeeting = contact && contact.isMeeting;

  const displayString =
    _get(contact, 'name') || _get(incomingCall, 'displayName') || _get(incomingCall, 'callerProperties.display_name');

  useEffect(() => {
    const setupMedia = async () => {
      try {
        if (!media.getPrimaryStream()) {
          // @ts-ignore
          dispatch(acquireWebcam());
        }
      } catch (ex) {
        logger.info('unable to capture stream in CallPreview', ex);
      }
    };
    // This will stop media if there are no incoming calls
    // We need to do this prior to invoking setupMedia()
    if (isVisible) {
      setupMedia();
    }
  }, [isVisible, bgSelection, defaultAudioMute, defaultVideoMute, dispatch, mediaSettings, vbbFeatureEnabled]);

  const renderAudioTag = () => (isCallConnected ? null : <AudioElement src={incomingRing} autoPlay loop />);

  const handleIgnore = () => {
    dispatch(stopWebcam());
    dispatch(callActions.ignore(call));
  };

  const renderButtons = () => {
    const buttons = [];
    if (call && call.canAdd) {
      buttons.push(
        <div key="add">
          <Button
            disabled={isLockedByModerator}
            onClick={() => {
              dispatch(callActions.add(call));
            }}
            dataAttrs={{ 'data-testid': 'add-call-btn' }}
          >
            {isLockedByModerator && <LockedMeetingTooltip />}
            <FormattedMessage id="components.call.add" defaultMessage="Add" />
          </Button>
        </div>
      );
    }

    if (isCallConnected) {
      buttons.push(
        <div key="endAndAnswer">
          <Button
            kind="secondary"
            onClick={() => {
              dispatch(closeFullScreenModal());
              setTimeout(() => {
                dispatch(callActions.endAndAnswer(call));
              }, 0);
            }}
            dataAttrs={{ 'data-testid': 'end-and-answer-call-btn' }}
          >
            <FormattedMessage id="components.call.endAndAnswer" defaultMessage="Leave + Answer" />
          </Button>
        </div>
      );
    }

    buttons.push(
      <div key="ignore">
        <Button kind="secondary" onClick={handleIgnore} dataAttrs={{ 'data-testid': 'ignore-call-btn' }}>
          <FormattedMessage id="components.call.ignore" defaultMessage="Ignore" />
        </Button>
      </div>
    );

    // Its the first call show answer button
    if (!isCallConnected) {
      buttons.push(
        <div key="answer">
          <Button
            disabled={isAcquiring || !localPrimaryStream}
            onClick={async () => {
              try {
              await dispatch(closeFullScreenModal());
              if (remoteModerationIsActive) {
                await dispatch(remoteModeratorActions.leaveCurrent());
              }
              dispatch(callActions.answer(call));
              } catch (e) {
                logger.debug(`Caught error while trying to answer the call, ${e}`);
              }
            }}
            dataAttrs={{ 'data-testid': 'answer-call-btn' }}
          >
            <FormattedMessage id="components.call.answer" defaultMessage="Answer" />
          </Button>
        </div>
      );
    }

    return <div className={classes.buttonBlock}>{buttons}</div>;
  }

  if (!isVisible) return null;

  if (isCallConnected) {
    return (
      <div className={classes.shortContainer}>
        {renderAudioTag()}
        <div className={classes.modalContent}>
          <button
            id="closeModalButton"
            className={classes.closeButton}
            aria-label="Close Full Screen Modal"
            onClick={handleIgnore}
          >
            <CancelIcon data-testid="cancelIcon" />
          </button>
          <article className={classes.callContainer}>
            <header>
              <h2 className={classes.modalHeader}>
                <FormattedMessage id="components.call.title" defaultMessage="Incoming Call" />
              </h2>
              <div className={classes.callName}>{displayString}</div>
            </header>
            <div className={classes.verticalButtons}>{renderButtons()}</div>
          </article>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {renderAudioTag()}
      <div className={classes.modalContent}>
        <button
          id="closeModalButton"
          className={classes.closeButton}
          aria-label="Close Full Screen Modal"
          onClick={handleIgnore}
        >
          <CancelIcon data-testid="cancelIcon" />
        </button>
        <article className={classes.callContainer}>
          <header>
            <h2 className={classes.modalHeader}>
              <FormattedMessage id="components.call.title" defaultMessage="Incoming Call" />
            </h2>
            <div className={classes.callName}>{displayString}</div>
          </header>
          <PulsingAvatar icon={isIncomingCallMeeting ? IconType.people : IconType.person} />
          <CallPreview />
          {renderButtons()}
        </article>
      </div>
    </div>
  );
}

export default IncomingCallModal;
